import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  FileText,
  Upload,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Building2,
  LogOut,
  Plus,
  Eye,
  Trash2,
  Calendar,
  DollarSign,
  FileUp,
  Loader2
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  recibida: { label: 'Recibida', color: 'bg-blue-100 text-blue-800', icon: Clock },
  en_revision: { label: 'En Revisión', color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle },
  autorizada: { label: 'Autorizada', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  rechazada: { label: 'Rechazada', color: 'bg-red-100 text-red-800', icon: XCircle },
  pagada: { label: 'Pagada', color: 'bg-emerald-100 text-emerald-800', icon: DollarSign }
};

const ProveedorDashboard = () => {
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoiceWindow, setInvoiceWindow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewInvoice, setShowNewInvoice] = useState(false);
  const [showInvoiceDetail, setShowInvoiceDetail] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [parks, setParks] = useState([]);

  const [newInvoice, setNewInvoice] = useState({
    park_id: '',
    numero_factura: '',
    fecha_factura: new Date().toISOString().split('T')[0],
    monto_subtotal: '',
    monto_iva: '',
    monto_total: '',
    concepto: ''
  });

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [profileRes, invoicesRes, windowRes] = await Promise.all([
        axios.get(`${API_URL}/api/suppliers/me/profile`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/invoices`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/suppliers/invoice-window`)
      ]);

      setProfile(profileRes.data);
      setInvoices(invoicesRes.data);
      setInvoiceWindow(windowRes.data);

      // Fetch parks assigned to this supplier - use parks from profile directly
      if (profileRes.data.parks?.length > 0) {
        setParks(profileRes.data.parks);
      } else if (profileRes.data.park_ids?.length > 0) {
        // Fallback: fetch parks directly if needed
        try {
          const parksRes = await axios.get(`${API_URL}/api/parks`, { headers: getAuthHeaders() });
          const assignedParks = parksRes.data.filter(p => profileRes.data.park_ids.includes(p.id));
          setParks(assignedParks);
        } catch {
          // If parks endpoint fails, we just won't have park names
          setParks(profileRes.data.park_ids.map(id => ({ id, nombre: 'Parque asignado' })));
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403) {
        toast.error('Su cuenta aún no ha sido aprobada');
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    logout();
  };

  const calculateIVA = (subtotal) => {
    const sub = parseFloat(subtotal) || 0;
    const iva = sub * 0.16;
    const total = sub + iva;
    setNewInvoice(prev => ({
      ...prev,
      monto_subtotal: subtotal,
      monto_iva: iva.toFixed(2),
      monto_total: total.toFixed(2)
    }));
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    if (!invoiceWindow?.allowed) {
      toast.error(invoiceWindow?.message || 'No es posible subir facturas en este momento');
      return;
    }

    setSubmitting(true);
    try {
      const invoiceData = {
        ...newInvoice,
        monto_subtotal: parseFloat(newInvoice.monto_subtotal),
        monto_iva: parseFloat(newInvoice.monto_iva),
        monto_total: parseFloat(newInvoice.monto_total)
      };

      const res = await axios.post(`${API_URL}/api/invoices`, invoiceData, { 
        headers: getAuthHeaders() 
      });
      
      toast.success('Factura creada exitosamente');
      setShowNewInvoice(false);
      setNewInvoice({
        park_id: '',
        numero_factura: '',
        fecha_factura: new Date().toISOString().split('T')[0],
        monto_subtotal: '',
        monto_iva: '',
        monto_total: '',
        concepto: ''
      });
      fetchData();

      // Open detail to upload files
      setShowInvoiceDetail({ id: res.data.invoice_id });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear factura');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = async (invoiceId, fileType, file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(
        `${API_URL}/api/invoices/${invoiceId}/upload-file?file_type=${fileType}`,
        formData,
        { 
          headers: { 
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      toast.success(`Archivo ${fileType.toUpperCase()} subido exitosamente`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Error al subir ${fileType}`);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('¿Está seguro de eliminar esta factura?')) return;

    try {
      await axios.delete(`${API_URL}/api/invoices/${invoiceId}`, { 
        headers: getAuthHeaders() 
      });
      toast.success('Factura eliminada');
      setShowInvoiceDetail(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-MX');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-slate-400 mx-auto mb-4" />
          <p className="text-slate-500">Cargando...</p>
        </div>
      </div>
    );
  }

  if (profile?.status === 'pendiente') {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/naave-logo.png" alt="NAAVE" style={{ height: '35px' }} />
            </div>
            <Button variant="ghost" onClick={handleLogout}>
              <LogOut size={18} className="mr-2" />
              Salir
            </Button>
          </div>
        </header>
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-slate-900 mb-4">
            Cuenta en Revisión
          </h1>
          <p className="text-slate-600 mb-6">
            Su solicitud de registro está siendo revisada por el administrador. 
            Le notificaremos por correo cuando su cuenta sea aprobada.
          </p>
          <div className="bg-slate-100 p-4 rounded text-left">
            <p className="text-sm text-slate-600">
              <strong>RFC:</strong> {profile?.rfc}<br />
              <strong>Razón Social:</strong> {profile?.razon_social}<br />
              <strong>Estado:</strong> <span className="text-amber-600 font-medium">Pendiente</span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (profile?.status === 'rechazado') {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/naave-logo.png" alt="NAAVE" style={{ height: '35px' }} />
            </div>
            <Button variant="ghost" onClick={handleLogout}>
              <LogOut size={18} className="mr-2" />
              Salir
            </Button>
          </div>
        </header>
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-slate-900 mb-4">
            Solicitud Rechazada
          </h1>
          <p className="text-slate-600 mb-6">
            Su solicitud de registro ha sido rechazada. 
            Contacte al administrador para más información.
          </p>
          {profile?.comentarios_revision && (
            <div className="bg-red-50 border border-red-200 p-4 rounded text-left">
              <p className="text-sm text-red-800">
                <strong>Motivo:</strong> {profile.comentarios_revision}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/naave-logo.png" alt="NAAVE" style={{ height: '35px' }} />
            <span className="text-slate-400 text-sm ml-2">Portal de Proveedores</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">{user?.nombre}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut size={18} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Profile Summary */}
        <div className="bg-white shadow-sm p-6 mb-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-900" data-testid="supplier-name">
                {profile?.razon_social}
              </h1>
              <p className="text-slate-500">RFC: {profile?.rfc}</p>
            </div>
            <div className="text-right">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                <CheckCircle size={14} className="mr-1" />
                Proveedor Aprobado
              </span>
            </div>
          </div>
        </div>

        {/* Invoice Window Status */}
        <div className={`p-4 mb-6 rounded ${
          invoiceWindow?.allowed 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-amber-50 border border-amber-200'
        }`}>
          <div className="flex items-center gap-3">
            <Calendar className={invoiceWindow?.allowed ? 'text-green-600' : 'text-amber-600'} />
            <div>
              <p className={`font-medium ${invoiceWindow?.allowed ? 'text-green-800' : 'text-amber-800'}`}>
                {invoiceWindow?.allowed ? 'Ventana de Recepción Activa' : 'Fuera de Ventana de Recepción'}
              </p>
              <p className={`text-sm ${invoiceWindow?.allowed ? 'text-green-600' : 'text-amber-600'}`}>
                {invoiceWindow?.message}
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-slate-900">Mis Facturas</h2>
          <Button
            onClick={() => setShowNewInvoice(true)}
            disabled={!invoiceWindow?.allowed || parks.length === 0}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="btn-new-invoice"
          >
            <Plus size={18} className="mr-2" />
            Nueva Factura
          </Button>
        </div>

        {parks.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 p-4 rounded mb-6">
            <p className="text-amber-800 text-sm">
              <AlertCircle className="inline mr-2" size={16} />
              No tiene parques asignados. Contacte al administrador para que le asigne los parques donde puede facturar.
            </p>
          </div>
        )}

        {/* Invoices List */}
        {invoices.length === 0 ? (
          <div className="bg-white shadow-sm p-12 text-center">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No tiene facturas registradas</p>
          </div>
        ) : (
          <div className="bg-white shadow-sm overflow-hidden">
            <table className="w-full" data-testid="invoices-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Folio</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Parque</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Fecha</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Total</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Estado</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Archivos</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {invoices.map((invoice) => {
                  const statusConfig = STATUS_CONFIG[invoice.status] || STATUS_CONFIG.recibida;
                  const StatusIcon = statusConfig.icon;
                  return (
                    <tr key={invoice.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">
                        {invoice.numero_factura}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {invoice.park_nombre}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {formatDate(invoice.fecha_factura)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-900 text-right font-medium">
                        {formatCurrency(invoice.monto_total)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${statusConfig.color}`}>
                          <StatusIcon size={12} className="mr-1" />
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <span className={`w-6 h-6 rounded flex items-center justify-center text-xs ${
                            invoice.pdf_url ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-400'
                          }`} title="PDF">
                            P
                          </span>
                          <span className={`w-6 h-6 rounded flex items-center justify-center text-xs ${
                            invoice.xml_url ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-400'
                          }`} title="XML">
                            X
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowInvoiceDetail(invoice)}
                          data-testid={`btn-view-invoice-${invoice.id}`}
                        >
                          <Eye size={16} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* New Invoice Dialog */}
      <Dialog open={showNewInvoice} onOpenChange={setShowNewInvoice}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nueva Factura</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateInvoice} className="space-y-4">
            <div>
              <Label>Parque *</Label>
              <Select
                value={newInvoice.park_id}
                onValueChange={(v) => setNewInvoice(prev => ({ ...prev, park_id: v }))}
              >
                <SelectTrigger data-testid="select-park">
                  <SelectValue placeholder="Seleccione parque" />
                </SelectTrigger>
                <SelectContent>
                  {parks.map(park => (
                    <SelectItem key={park.id} value={park.id}>
                      {park.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Número de Factura *</Label>
                <Input
                  value={newInvoice.numero_factura}
                  onChange={(e) => setNewInvoice(prev => ({ ...prev, numero_factura: e.target.value }))}
                  placeholder="ABC-001"
                  required
                  data-testid="input-invoice-number"
                />
              </div>
              <div>
                <Label>Fecha de Factura *</Label>
                <Input
                  type="date"
                  value={newInvoice.fecha_factura}
                  onChange={(e) => setNewInvoice(prev => ({ ...prev, fecha_factura: e.target.value }))}
                  required
                  data-testid="input-invoice-date"
                />
              </div>
            </div>

            <div>
              <Label>Concepto *</Label>
              <Input
                value={newInvoice.concepto}
                onChange={(e) => setNewInvoice(prev => ({ ...prev, concepto: e.target.value }))}
                placeholder="Descripción del servicio"
                required
                data-testid="input-concepto"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Subtotal *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newInvoice.monto_subtotal}
                  onChange={(e) => calculateIVA(e.target.value)}
                  placeholder="0.00"
                  required
                  data-testid="input-subtotal"
                />
              </div>
              <div>
                <Label>IVA (16%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newInvoice.monto_iva}
                  readOnly
                  className="bg-slate-50"
                  data-testid="input-iva"
                />
              </div>
              <div>
                <Label>Total</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newInvoice.monto_total}
                  readOnly
                  className="bg-slate-50 font-medium"
                  data-testid="input-total"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowNewInvoice(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="btn-create-invoice"
              >
                {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                Crear Factura
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invoice Detail Dialog */}
      <Dialog open={!!showInvoiceDetail} onOpenChange={() => setShowInvoiceDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalle de Factura</DialogTitle>
          </DialogHeader>
          {showInvoiceDetail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Folio:</span>
                  <p className="font-medium">{showInvoiceDetail.numero_factura}</p>
                </div>
                <div>
                  <span className="text-slate-500">Fecha:</span>
                  <p className="font-medium">{formatDate(showInvoiceDetail.fecha_factura)}</p>
                </div>
                <div>
                  <span className="text-slate-500">Parque:</span>
                  <p className="font-medium">{showInvoiceDetail.park_nombre}</p>
                </div>
                <div>
                  <span className="text-slate-500">Estado:</span>
                  <p className="font-medium">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${
                      STATUS_CONFIG[showInvoiceDetail.status]?.color || 'bg-slate-100'
                    }`}>
                      {STATUS_CONFIG[showInvoiceDetail.status]?.label || showInvoiceDetail.status}
                    </span>
                  </p>
                </div>
              </div>

              <div className="border-t pt-4">
                <p className="text-slate-500 text-sm mb-1">Concepto:</p>
                <p className="font-medium">{showInvoiceDetail.concepto}</p>
              </div>

              <div className="grid grid-cols-3 gap-4 border-t pt-4">
                <div className="text-center">
                  <p className="text-slate-500 text-xs">Subtotal</p>
                  <p className="font-medium">{formatCurrency(showInvoiceDetail.monto_subtotal)}</p>
                </div>
                <div className="text-center">
                  <p className="text-slate-500 text-xs">IVA</p>
                  <p className="font-medium">{formatCurrency(showInvoiceDetail.monto_iva)}</p>
                </div>
                <div className="text-center">
                  <p className="text-slate-500 text-xs">Total</p>
                  <p className="font-bold text-lg">{formatCurrency(showInvoiceDetail.monto_total)}</p>
                </div>
              </div>

              {/* File Uploads */}
              <div className="border-t pt-4 space-y-3">
                <p className="text-sm font-medium text-slate-700">Archivos de Factura</p>
                
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded">
                  <div className="flex items-center gap-2">
                    <FileUp size={18} className="text-slate-400" />
                    <span className="text-sm">PDF de Factura</span>
                  </div>
                  {showInvoiceDetail.pdf_url ? (
                    <a
                      href={showInvoiceDetail.pdf_url}
                      download={`factura_${showInvoiceDetail.numero_factura}.pdf`}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Descargar
                    </a>
                  ) : showInvoiceDetail.status === 'recibida' ? (
                    <label className="cursor-pointer text-sm text-blue-600 hover:underline">
                      Subir PDF
                      <input
                        type="file"
                        accept=".pdf"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            handleFileUpload(showInvoiceDetail.id, 'pdf', e.target.files[0]);
                          }
                        }}
                      />
                    </label>
                  ) : (
                    <span className="text-sm text-slate-400">No subido</span>
                  )}
                </div>

                <div className="flex items-center justify-between p-3 bg-slate-50 rounded">
                  <div className="flex items-center gap-2">
                    <FileUp size={18} className="text-slate-400" />
                    <span className="text-sm">XML de Factura</span>
                  </div>
                  {showInvoiceDetail.xml_url ? (
                    <a
                      href={showInvoiceDetail.xml_url}
                      download={`factura_${showInvoiceDetail.numero_factura}.xml`}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Descargar
                    </a>
                  ) : showInvoiceDetail.status === 'recibida' ? (
                    <label className="cursor-pointer text-sm text-blue-600 hover:underline">
                      Subir XML
                      <input
                        type="file"
                        accept=".xml"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            handleFileUpload(showInvoiceDetail.id, 'xml', e.target.files[0]);
                          }
                        }}
                      />
                    </label>
                  ) : (
                    <span className="text-sm text-slate-400">No subido</span>
                  )}
                </div>
              </div>

              {/* Comments if rejected */}
              {showInvoiceDetail.status === 'rechazada' && showInvoiceDetail.comentarios && (
                <div className="bg-red-50 border border-red-200 p-3 rounded">
                  <p className="text-sm text-red-800">
                    <strong>Motivo de rechazo:</strong> {showInvoiceDetail.comentarios}
                  </p>
                </div>
              )}

              {/* Delete option only for 'recibida' status */}
              {showInvoiceDetail.status === 'recibida' && (
                <div className="border-t pt-4 flex justify-end">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDeleteInvoice(showInvoiceDetail.id)}
                    data-testid="btn-delete-invoice"
                  >
                    <Trash2 size={16} className="mr-2" />
                    Eliminar Factura
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProveedorDashboard;
